import { useEffect, useRef } from 'react';

import {
	getLoomProvider,
	type LoomProviderConfig,
} from '@confluence/fabric-providers/entry-points/getLoomProvider';
import { useLoomEntryPointVariant } from '@confluence/loom-utils';
import { fg } from '@confluence/feature-gating';

import type { EditorAPI } from '../loomEditorUtils-types';

type UseInitializeLoomPlugin = {
	loomPlugin?: EditorAPI['loom'];
	providerConfig: LoomProviderConfig;
};

export const useInitializeLoomPlugin = ({
	loomPlugin,
	providerConfig,
}: UseInitializeLoomPlugin) => {
	const hasInitialized = useRef(false);

	const { entryPointVariant } = useLoomEntryPointVariant();
	const isLoomRecordingEligible = ['BETA', 'CO_USE'].includes(entryPointVariant);

	const initLoom = loomPlugin?.actions.initLoom;

	// If confluence_frontend_loom_allow_multiple_init is on, we don't want to rely on the useEffect hook below to
	// initialize loom. Instead we will check the plugin state and re-initialize loom if its not ready.
	if (fg('confluence_frontend_loom_allow_multiple_init')) {
		const isLoomEnabled = loomPlugin?.sharedState?.currentState()?.isEnabled;
		if (initLoom && !isLoomEnabled) {
			void initLoom({ loomProvider: getLoomProvider(providerConfig) });
		}
	}

	useEffect(() => {
		if (fg('confluence_frontend_loom_allow_multiple_init')) {
			return;
		}

		if (!hasInitialized.current && initLoom && isLoomRecordingEligible) {
			hasInitialized.current = true;
			void initLoom({ loomProvider: getLoomProvider(providerConfig) });
		}
	}, [initLoom, isLoomRecordingEligible, providerConfig]);
};
